// Libs
import classNames from 'classnames/bind';
import { useTranslation } from 'react-i18next';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { useEffect, useMemo, useState } from 'react';
import { Box, Button, Grid2, Stack, Typography } from '@mui/material';
import { useParams } from 'react-router-dom';
// Components, Layouts, Pages
import { EstimateMaterialsComponent, EstimateServiceComponent } from '~/components/module/estimate';
import { UnderDevelopment } from '~/components/common';
// Others
import { DEFAULT_NUMBER_ZERO, EMPTY_STRING } from '~/utils/constants/common';
import { TypeTabCreateEstimateEnum } from '~/utils/enum/common';
import { IFormEstimate } from '~/utils/interface/estimate';
import {
  defaultValuesFormMaterial,
  defaultValuesFormService,
} from '~/pages/template/formEstimate/helper';
import { formatCurrency } from '~/utils/helper/common';
// Styles, images, icons
import styles from './CreateEstimateLineItemTab.module.scss';
import { icons } from '~/assets';

type Props = {
  onEdit?: (data: IFormEstimate, type: string) => void;
  onCancelEdit: () => void;
};

const cx = classNames.bind(styles);

const CreateEstimateLineItemTab = (props: Props) => {
  //#region Destructuring Props
  const { onEdit, onCancelEdit } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/estimate', 'common/base']);
  const { id: estimateId } = useParams();
  const {
    control,
    setValue,
    getValues,
    formState: { isDirty },
  } = useFormContext<IFormEstimate>();

  const projectedServices = useWatch({ control, name: 'projectedServices' });
  const projectedMaterials = useWatch({ control, name: 'projectedMaterials' });
  // const discounts = useWatch({ control, name: 'discounts' });

  const {
    fields: fieldsServices,
    append: appendService,
    remove: removeService,
  } = useFieldArray({ control, name: 'projectedServices' });

  const {
    fields: fieldsMaterials,
    append: appendMaterials,
    remove: removeMaterials,
  } = useFieldArray({ control, name: 'projectedMaterials' });

  // const {
  //   fields: fieldsDiscounts,
  //   append: appendDiscounts,
  //   remove: removeDiscounts,
  // } = useFieldArray({ control, name: 'discounts' });
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [isShowUnderDevelopment, setIsShowUnderDevelopment] = useState<boolean>(false);
  //#endregion Declare State

  //#region Declare Memo
  const subtotal = useMemo(() => {
    return [...(projectedServices || []), ...(projectedMaterials || [])].reduce((sum, item) => {
      const price = Number(item.price) || DEFAULT_NUMBER_ZERO;
      const quantity = Number(item.quantity) || DEFAULT_NUMBER_ZERO;
      return sum + price * quantity;
    }, DEFAULT_NUMBER_ZERO);
  }, [projectedServices, projectedMaterials]);

  // const totalDiscount = useMemo(() => {
  //   if (!discounts || discounts.length === DEFAULT_NUMBER_ZERO) return DEFAULT_NUMBER_ZERO;

  //   const { totalPercentage, totalDollar } = discounts.reduce(
  //     (acc, item) => {
  //       const rate = Number(item.rate) || DEFAULT_NUMBER_ZERO;
  //       if (item.type === TypeDiscountEnum.PERCENTAGE) {
  //         acc.totalPercentage += rate;
  //       } else if (item.type === TypeDiscountEnum.DOLLAR) {
  //         acc.totalDollar += rate;
  //       }
  //       return acc;
  //     },
  //     { totalPercentage: DEFAULT_NUMBER_ZERO, totalDollar: DEFAULT_NUMBER_ZERO }
  //   );

  //   return (subtotal * totalPercentage) / 100 + totalDollar;
  // }, [subtotal, discounts]);

  const total = useMemo(() => subtotal, [subtotal]);
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    setValue('subtotal', Number(subtotal), { shouldValidate: true });
  }, [subtotal, setValue]);

  useEffect(() => {
    setValue('total', Number(total), { shouldValidate: true });
  }, [total, setValue]);

  //#endregion Implement Hook

  //#region Handle Function
  const handleCloseUnderDevelopment = () => {
    setIsShowUnderDevelopment(false);
  };

  const handleAddItemService = () => {
    appendService(defaultValuesFormService);
  };

  const handleAddItemMaterials = () => {
    appendMaterials(defaultValuesFormMaterial);
  };

  // const handleAddItemDiscounts = () => {
  //   appendDiscounts(defaultValuesFormDiscount);
  // };

  const handleSubmitFormLineItem = () => {
    const dataForm = getValues();
    const dataBody: IFormEstimate = {
      clientId: EMPTY_STRING,
      projectedServices: dataForm?.projectedServices || [],
      projectedMaterials: dataForm?.projectedMaterials || [],
      subtotal: Number(dataForm.subtotal) || DEFAULT_NUMBER_ZERO,
      total: Number(dataForm?.total || DEFAULT_NUMBER_ZERO),
    };

    onEdit && onEdit(dataBody, TypeTabCreateEstimateEnum.LINE_ITEMS);
  };
  //#endregion Handle Function

  return (
    <Box component={'div'} className={cx('formBody')}>
      <Grid2 container rowSpacing={'24px'} columnSpacing={'24px'}>
        <Grid2 container size={12} rowSpacing={'24px'} columnSpacing={'12px'}>
          <Grid2 size={12}>
            <Typography color='primary' className={cx('titleLienItem')}>
              {t('line_item_title')}
            </Typography>
          </Grid2>

          <Grid2 container size={12} rowSpacing={'20px'}>
            <Grid2 size={12}>
              <Typography
                color='primary'
                className={cx(
                  'groupTitle',
                  fieldsServices?.length !== DEFAULT_NUMBER_ZERO ? 'border' : EMPTY_STRING
                )}
              >
                {t('field_service_title')}
              </Typography>
            </Grid2>

            <Grid2 container size={12} rowSpacing={'12px'} columnSpacing={'12px'}>
              {fieldsServices?.map((item, index) => (
                <EstimateServiceComponent index={index} key={item.id} onRemoveItem={removeService} />
              ))}

              <Grid2 size={12}>
                <Stack
                  direction={'row'}
                  alignItems={'center'}
                  spacing={'4px'}
                  className={cx('addFieldGroup')}
                  onClick={handleAddItemService}
                >
                  <img
                    src={icons.commonIconAddField}
                    alt={t('altImg', { ns: 'common/base' })}
                    className={cx('addFieldIcon')}
                  />

                  <Typography color='primary' className={cx('addField')}>
                    {t('common_action_add_field', { ns: 'common/base' })}
                  </Typography>
                </Stack>
              </Grid2>
            </Grid2>
          </Grid2>

          <Grid2 container size={12} rowSpacing={'20px'}>
            <Grid2 size={12}>
              <Typography
                color='primary'
                className={cx(
                  'groupTitle',
                  fieldsMaterials?.length !== DEFAULT_NUMBER_ZERO ? 'border' : EMPTY_STRING
                )}
              >
                {t('field_materials_title')}
              </Typography>
            </Grid2>

            <Grid2 container size={12} rowSpacing={'12px'} columnSpacing={'12px'}>
              {fieldsMaterials?.map((item, index) => (
                <EstimateMaterialsComponent index={index} key={item.id} onRemoveItem={removeMaterials} />
              ))}

              <Grid2 size={12}>
                <Stack
                  direction={'row'}
                  alignItems={'center'}
                  spacing={'4px'}
                  className={cx('addFieldGroup')}
                  onClick={handleAddItemMaterials}
                >
                  <img
                    src={icons.commonIconAddField}
                    alt={t('altImg', { ns: 'common/base' })}
                    className={cx('addFieldIcon')}
                  />

                  <Typography color='primary' className={cx('addField')}>
                    {t('common_action_add_field', { ns: 'common/base' })}
                  </Typography>
                </Stack>
              </Grid2>
            </Grid2>
          </Grid2>

          {/* <Box className={cx('item')}>
            <Box className={cx('textTitle')}>{t('field_subtotal_label')}</Box>
            <Box className={cx('textTitle')}>{formatCurrency(subtotal)}</Box>
          </Box> */}

          {/* <Grid2 container size={12} rowSpacing={'12px'}>
            <Grid2 size={12}>
              <Typography
                color='primary'
                className={cx(
                  'groupTitle',
                  fieldsDiscounts?.length !== DEFAULT_NUMBER_ZERO ? 'border' : EMPTY_STRING
                )}
              >
                {t('field_discounts_title')}
              </Typography>
            </Grid2>

            <Grid2 container size={12} rowSpacing={'12px'} columnSpacing={'12px'}>
              {fieldsDiscounts?.map((item, index) => (
                <EstimateDiscountComponent index={index} key={item.id} onRemoveItem={removeDiscounts} />
              ))}

              <Grid2 size={12}>
                <Stack
                  direction={'row'}
                  alignItems={'center'}
                  spacing={'4px'}
                  className={cx('addFieldGroup')}
                  onClick={handleAddItemDiscounts}
                >
                  <img
                    src={icons.commonIconAddField}
                    alt={t('altImg', { ns: 'common/base' })}
                    className={cx('addFieldIcon')}
                  />

                  <Typography color='primary' className={cx('addField')}>
                    {t('common_action_add_field', { ns: 'common/base' })}
                  </Typography>
                </Stack>
              </Grid2>
            </Grid2>
          </Grid2> */}

          <Box className={cx('item')}>
            <Box className={cx('textTitleTotal')}>{t('field_total_label')}</Box>
            <Box className={cx('textTitleTotal')}>{formatCurrency(total)}</Box>
          </Box>

          <Box className={cx('groupTitleFooter')}>{t('line_item_text_footer')}</Box>

          {estimateId && (
            <Stack
              direction={'row'}
              alignItems={'center'}
              justifyContent={'flex-end'}
              component={'div'}
              spacing={'12px'}
              className={cx('formFooter')}
            >
              <Button variant='outlined' onClick={onCancelEdit}>
                {t('common_action_cancel', { ns: 'common/base' })}
              </Button>

              <Button
                variant='contained'
                type='button'
                onClick={handleSubmitFormLineItem}
                disabled={!isDirty}
              >
                {t('common_action_save', { ns: 'common/base' })}
              </Button>
            </Stack>
          )}
        </Grid2>
      </Grid2>

      <UnderDevelopment isOpen={isShowUnderDevelopment} onClose={handleCloseUnderDevelopment} />
    </Box>
  );
};

export default CreateEstimateLineItemTab;
